import * as React from "react";
import { useField } from "formik";
import { useConditionDispatch } from "../../context/Condition.context";

// Components
import IconCheckboxArrow from "../Icons/IconCheckboxArrow";
import Label from "../Label/Label";
import ErrorText from "../ErrorText/ErrorText";

// Interfaces
import Answer from "../../types/Answer.interface";
import Feedback from "../../types/Feedback.interface";

// Misc.
import { getArrayIndex, isPresentInArray } from "../../lib/Array.lib";
import useFeedback from "../../lib/useFeedback.lib";

// Styles
import "./InputCheckbox.scss";
import FeedbackText from "../FeedbackText/FeedbackText";

interface InputCheckbox {
  id: string;
  label: string;
  answers: Answer[];
  feedback: Feedback[];
}

interface TestInput {
  [key: string]: string;
}

const InputCheckbox: React.FC<InputCheckbox> = (props: InputCheckbox) => {
  const { id, label, answers, feedback } = props;
  const [field, meta, helper] = useField(id);
  const [feedbackData] = useFeedback(feedback, field);

  const [inputField, setInputField] = React.useState<TestInput>({});
  const [inputPristine, setInputPristine] = React.useState(true);
  const condDispatch = useConditionDispatch();

  const checkMetaValueAndSetInput = (answer: Answer) => {
    const indexInArray = getArrayIndex(meta.initialValue, answer._id);

    if (
      indexInArray >= 0 &&
      inputField[answer._id] === undefined &&
      inputPristine
    ) {
      setInputField({
        ...inputField,
        [answer._id]: meta.initialValue[indexInArray].value,
      });
    }
  };

  const handleCheckboxChange = (answerId: string, value: string) => {
    const newFieldValue = { id: answerId, value };
    let newValue = {};

    if (isPresentInArray(field.value, answerId)) {
      newValue = field.value.filter((item: any) => item.id !== answerId);
    } else {
      newValue = [...field.value, newFieldValue];
    }

    condDispatch({
      type: "add",
      payload: { [id]: newValue },
    });

    helper.setValue(newValue);
  };

  const renderCheckboxAnswer = (answer: Answer): React.ReactNode => {
    if (answer.answer.includes("%text_field%")) {
      const textFieldRegex = new RegExp(/(%text_field%)/, "g");
      const formattedAnswer = answer.answer.split(textFieldRegex);
      checkMetaValueAndSetInput(answer);

      return (
        <span className="leading-tight">
          {formattedAnswer.map((part: string, index: number) => {
            return part.match(textFieldRegex) ? (
              <input
                key={`${answer._id}-custom`}
                type="text"
                className="py-1 px-3 text-dw-grey-14 inline-block rounded-none"
                id={`${answer._id}-custom`}
                value={inputField[answer._id] || ""}
                maxLength={150}
                onFocus={(e) => {
                  if (!isPresentInArray(field.value, answer._id)) {
                    helper.setValue([
                      ...field.value,
                      { id: answer._id, value: e.target.value },
                    ]);
                  }
                }}
                onChange={(e) => {
                  const newValue = field.value.map((fieldValue: any) => {
                    if (fieldValue.id === answer._id) {
                      /**
                       * If Checkbox Input is changed, falsify `pristine` to prevent
                       * Input to be overwritten from Function `checkMetaValueAndSetInput`
                       * when this input field is cleared
                       */
                      if (inputPristine) setInputPristine(false);

                      setInputField({
                        ...inputField,
                        [answer._id]: e.target.value,
                      });

                      return { ...fieldValue, value: e.target.value };
                    }

                    return fieldValue;
                  });

                  helper.setValue(newValue);
                }}
                checked={isPresentInArray(field.value, answer._id)}
              />
            ) : (
              part
            );
          })}
        </span>
      );
    }

    return <span className="leading-tight">{answer.answer}</span>;
  };

  return (
    <fieldset className="checkbox">
      <Label as="legend">{label}</Label>
      {answers.map((answer) => {
        const includesTextField = answer.answer.includes("%text_field%");

        return (
          <div key={answer._id} className="my-4 last:mb-0 flex items-center">
            <input
              type="checkbox"
              className="mr-2"
              id={answer._id}
              {...field}
              checked={isPresentInArray(field.value, answer._id)}
              value={includesTextField ? inputField[answer._id] : answer.answer}
              onChange={(e) => {
                handleCheckboxChange(answer._id, e.target.value);
              }}
            />
            <label
              htmlFor={answer._id}
              className="cursor-pointer block w-full text-dw-grey-14"
            >
              <div className="checkbox__custom">
                <IconCheckboxArrow />
              </div>
              {renderCheckboxAnswer(answer)}
            </label>
          </div>
        );
      })}
      {!!meta.error && !!meta.touched && (
        <ErrorText text="This field is required" />
      )}
      {feedbackData && field.value.length > 0 && (
        <FeedbackText text={feedbackData} />
      )}
    </fieldset>
  );
};

export default InputCheckbox;
